import { push } from 'react-router-redux';
import { all, call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';

import { human, loggedIn } from '@opendoor/shared-fe/build/store/session/selectors';
import * as api from '../../api';
import * as actions from './actions';
import * as actionTypes from './actionTypes';
import * as selectors from './selectors';

function* fetchBrokerageOfferData(action) {
  yield put(actions.beginLoadingBrokerageOfferData());

  try {
    const brokerageOfferData = yield call(api.fetchBrokerageOffer, action.payload.token);
    yield put(actions.receiveBrokerageOfferData(brokerageOfferData));
  } catch (e) {
    yield put(actions.redirectToError('fetch_brokerage_data'));
  }
}

function* fetchOtherBids(action) {
  try {
    const otherBidsData = yield call(api.fetchOtherBids, action.payload.token);
    yield put(actions.receiveOtherBidsData(otherBidsData));
  } catch (e) {
    // Let's show no other bids instead of breaking the app if the call fails for whatever reason
    yield put(actions.receiveOtherBidsData([]));
  }
}

// This function to be called on app load so that the invitation token is accessible through the store.
function* storeInvitationTokenFromLocalStorage(action) {
  const brokerageOfferToken = action.payload.token;
  const localStorageKey = `brokerage-offer.${brokerageOfferToken}.invitation_token`;
  const invitationToken = localStorage.getItem(localStorageKey);
  if (!invitationToken) {
    return;
  }
  yield put(actions.setInvitationToken(invitationToken, brokerageOfferToken));
}

function* setInvitationToken(action) {
  const { brokerageOfferToken, invitationToken } = action.payload;
  const localStorageKey = `brokerage-offer.${brokerageOfferToken}.invitation_token`;
  localStorage.setItem(localStorageKey, invitationToken);

  // Fetch invitation from the server
  const invitationData = yield call(api.fetchInvitation, invitationToken);
  if (!invitationData || !invitationData.id) {
    return;
  }
  yield put(actions.receiveInvitationData(invitationData));
}

function* beginOfferAccept(action) {
  // The seller clicked on the "Next" button from the offer details page. Figure out where to go from here.
  const brokerageOfferToken = yield select(selectors.brokerageOfferToken);
  const userIsLoggedIn = yield select(loggedIn);
  const invitationData = yield select(selectors.invitationData);

  // Not logged in
  if (!userIsLoggedIn) {
    if (invitationData) {
      yield put(push(`/brokerage-offers/${brokerageOfferToken}/account`));
    } else {
      yield put(actions.redirectToError('begin_offer_accept_no_invitation'));
    }
    return;
  }

  // User is logged in
  const hasCompletedPurchaseAgreement = yield select(selectors.hasCompletedPurchaseAgreement);

  if (!hasCompletedPurchaseAgreement) {
    yield put(push(`/brokerage-offers/${brokerageOfferToken}/purchase-agreement`));
    return;
  }

  // Has already completed purchase agreement
  const emailMatchesInvitation = yield select(selectors.humanMatchesInvitation);
  if (emailMatchesInvitation) {
    // Don't allow user to resubmit purchase agreement. Go directly to "You're all set".
    yield put(push(`/brokerage-offers/${brokerageOfferToken}/complete`));
  } else {
    yield put(actions.redirectToError('purchase_agreement_completed_email_mismatch'));
  }
}

function* maybeAcceptInvite(action) {
  // Called after PurchaseAgreement is loaded. Due to different authentication cases, we need to handle the following
  // cases:
  // 1) redirect the user to log in or create an account if they aren't logged in,
  // 2) accept the invitation for the user if they're already logged in, or
  // 3) simply render the purchase agreement page if (1) and (2) are already done
  const userIsLoggedIn = yield select(loggedIn);

  if (!userIsLoggedIn) {
    // Redirect to Account page
    const brokerageOfferToken = yield select(selectors.brokerageOfferToken);
    yield put(push(`/brokerage-offers/${brokerageOfferToken}/account`));
    return;
  }

  // User is logged in
  const invitationData = yield select(selectors.invitationData);

  // invitationData needs to be loaded on page load, so let's do a sanity check to make sure that it actually exists
  // before accessing it.
  if (!invitationData) {
    return;
  }

  if (invitationData.get('invitation_accepted')) {
    const emailMatches = yield select(selectors.humanMatchesInvitation);
    if (!emailMatches) {
      yield put(actions.redirectToError('accept_invitation_email_mismatch'));
    }
    return;
  }

  // Invitation not accepted yet. Let's accept it!
  yield put(actions.acceptInvitation());
}

function* acceptInvite(action) {
  // Make the network call to accept the invitation. The user must be logged in at this point.
  const invitationToken = yield select(selectors.invitationToken);
  const humanModel = yield select(human);
  const humanEmail = humanModel.get('email');

  try {
    const invitationData = yield call(api.acceptInvite, invitationToken, humanEmail);
    yield put(actions.receiveInvitationData(invitationData));
  } catch (e) {
    yield put(actions.redirectToError('accept_invitation_api_error'));
  }
}

function* createNewUserAccount(action) {
  const { email, password } = action.payload;
  const brokerageOfferToken = yield select(selectors.brokerageOfferToken);
  const invitationToken = yield select(selectors.invitationToken);

  try {
    yield call(api.acceptInvite, invitationToken, email, password);
  } catch (e) {
    yield put(actions.redirectToError('create_new_user_api_error'));
    return;
  }

  // Because the session is reset after authenticating a user, we have a different CSRF token. We'll do a full page
  // redirect to the purchase agreement from here.
  window.location.href = `/brokerage-offers/${brokerageOfferToken}/purchase-agreement`;
}

function* handleReturningUser(action) {
  const { email, password } = action.payload;
  const logInResponse = yield call(api.logIn, email, password);

  if (logInResponse && logInResponse.error) {
    yield put(actions.loginError(logInResponse.error));
  } else {
    const brokerageOfferToken = yield select(selectors.brokerageOfferToken);
    // See comment in createNewUserAccount()
    window.location.href = `/brokerage-offers/${brokerageOfferToken}/purchase-agreement`;
  }
}

function* onDeclineSubmit(action) {
  const { declineReasons, feedbackText } = action.payload;
  const brokerageOfferToken = yield select(selectors.brokerageOfferToken);
  const brokerageOfferData = yield call(
    api.saveBrokerageOfferFeedback,
    brokerageOfferToken,
    [...declineReasons],
    feedbackText,
  );
  yield put(actions.receiveBrokerageOfferData(brokerageOfferData));
  yield put(actions.hideDeclineOfferModal());
}

function* redirectToError(action) {
  const brokerageOfferToken = yield select(selectors.brokerageOfferToken);
  const isLoggedIn = yield select(loggedIn);
  const humanModel = yield select(human);
  const { error } = action.payload;
  const email = isLoggedIn ? humanModel.get('email') : null;

  yield put(push(`/brokerage-offers/${brokerageOfferToken}/error`));

  if (email && email.indexOf('opendoor.com') >= 0) {
    return;
  }

  // Report what error occurred so we can create a Slack notification about it to #eng-alerts-bus-serv
  const postData = {
    brokerageOfferToken,
    email,
    error,
  };
  yield call(api.reportError, postData);
}

function* requestNewOffer() {
  const brokerageOfferToken = yield select(selectors.brokerageOfferToken);
  const leadToken = yield select(selectors.leadToken);
  const leadStatus = yield select(selectors.leadStatus);
  try {
    yield call(api.requestNewOffer, brokerageOfferToken);
    if (leadStatus === 'denied') {
      yield put(push(`/seller-lead/${leadToken}`));
    } else {
      yield put(actions.setNewOfferRequested());
    }
  } catch (e) {
    yield put(actions.redirectToError('request_new_offer_api_error'));
  }
}

function* submitPurchaseAgreement() {
  const brokerageOfferToken = yield select(selectors.brokerageOfferToken);
  const purchaseAgreement = yield select(selectors.purchaseAgreement);

  try {
    yield call(api.submitPurchaseAgreement, brokerageOfferToken, purchaseAgreement);
    yield put(actions.redirectToComplete());
  } catch (e) {
    yield put(actions.redirectToError('submit_purchase_agreement_api_error'));
  }
}

function* redirectToComplete() {
  const brokerageOfferToken = yield select(selectors.brokerageOfferToken);
  yield put(push(`/brokerage-offers/${brokerageOfferToken}/complete`));
}

export default function* saga() {
  yield all([
    takeLatest(actionTypes.FETCH_BROKERAGE_OFFER_DATA, fetchBrokerageOfferData),
    takeEvery(actionTypes.SET_INVITATION_TOKEN, setInvitationToken),
    takeEvery(
      actionTypes.STORE_INVITATION_TOKEN_FROM_LOCAL_STORAGE,
      storeInvitationTokenFromLocalStorage,
    ),
    takeLatest(actionTypes.BEGIN_OFFER_ACCEPT, beginOfferAccept),
    takeLatest(actionTypes.MAYBE_ACCEPT_INVITATION, maybeAcceptInvite),
    takeLatest(actionTypes.ACCEPT_INVITATION, acceptInvite),
    takeLatest(actionTypes.CREATE_NEW_USER_ACCOUNT, createNewUserAccount),
    takeLatest(actionTypes.HANDLE_RETURNING_USER, handleReturningUser),
    takeLatest(actionTypes.ON_DECLINE_SUBMIT, onDeclineSubmit),
    takeLatest(actionTypes.REDIRECT_TO_ERROR, redirectToError),
    takeLatest(actionTypes.FETCH_OTHER_BIDS, fetchOtherBids),
    takeLatest(actionTypes.REQUEST_NEW_OFFER, requestNewOffer),
    takeLatest(actionTypes.SUBMIT_PURCHASE_AGREEMENT, submitPurchaseAgreement),
    takeLatest(actionTypes.REDIRECT_TO_COMPLETE, redirectToComplete),
  ]);
}
